import { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import {
    Box,
    Button,
    Drawer,
    TextField,
    Typography,
    IconButton,
    Paper,
    CircularProgress,
    Popover,
    MenuItem,
    Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import CustomerService from '../services/CustomerService';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ConfirmModal from './modal/ConfirmModal';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import styles from "./css/Customers.module.css";
import PageHeader from './PageHeader';
import AddressAutocomplete from "./sub-components/AddressAutocomplete";
import InvoiceOnboardingModal from './modal/InvoiceOnboardingModal';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const EMPTY_CUSTOMER = {
    name: '',
    email: '',
    phone: '',
    website: '',
    foundationYear: '',
    employeesNr: '',
    address: '',
    customerContact: [
        { firstName: '', lastName: '', email: '', phone: '', address: '' },
    ],
};

const CustomersPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalNumberOfCustomers, setTotalNumberOfCustomers] = useState(0);
    const [page, setPage] = useState(0);
    const [filter, setFilter] = useState('');
    const [activeSearch, setActiveSearch] = useState('');

    const [highlightId, setHighlightId] = useState(null);
    const highlightTimerRef = useRef(null);

    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const [isOnboardingOpen, setOnboardingOpen] = useState(false);
    const [customerData, setCustomerData] = useState(EMPTY_CUSTOMER);
    const [validationErrors, setValidationErrors] = useState({});

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [anchorCustomerEl, setAnchorCustomerEl] = useState(null);
    const [isConfirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [isDeletingCustomer, setIsDeletingCustomer] = useState(false);

    const PAGE_SIZE = 12;

    const fetchPage = async (pageNum, searchText, append = false) => {
        append ? setLoadingMore(true) : setLoadingCustomers(true);
        try {
            const data = searchText
                ? (await CustomerService.searchCustomers({ searchText }, pageNum, PAGE_SIZE)).data
                : await CustomerService.getCustomers(pageNum, PAGE_SIZE);
            setCustomers(prev => append ? [...prev, ...(data.content || [])] : (data.content || []));
            setTotalNumberOfCustomers(data.totalElements ?? 0);
            setPage(pageNum);
        } catch (error) {
            showToast(t('customers.messages.loadError'), 'warning');
            console.error('Failed to fetch customers:', error);
        } finally {
            append ? setLoadingMore(false) : setLoadingCustomers(false);
        }
    };

    useEffect(() => {
        fetchPage(0, '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
        const text = filter.trim();
        setActiveSearch(text);
        fetchPage(0, text);
    };

    const handleLoadMore = () => fetchPage(page + 1, activeSearch, true);

    // Drawer open/close
    const handleOpenDrawer = () => setDrawerOpen(true);

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setCustomerData(EMPTY_CUSTOMER);
        setValidationErrors({});
    };

    const updateContact = (field, value) => {
        const updated = [...customerData.customerContact];
        updated[0] = { ...updated[0], [field]: value };
        setCustomerData(prev => ({ ...prev, customerContact: updated }));
    };

    // Validation
    const validateInputs = () => {
        const errors = {};
        const currentYear = new Date().getFullYear();
        const urlPattern = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:[0-9]+)?(\/[\w\-./?=%&]*)?$/i;
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phonePattern = /^\+?[0-9\s\-()]+$/;
        const isEmpty = (v) => !v || String(v).trim() === '';

        if (isEmpty(customerData.name))  errors.name  = t('customers.validation.companyNameRequired');
        if (isEmpty(customerData.email)) errors.email = t('customers.validation.emailRequired');
        else if (!emailPattern.test(customerData.email)) errors.email = t('customers.validation.invalidEmail');
        if (!isEmpty(customerData.phone) && !phonePattern.test(customerData.phone)) errors.phone = t('customers.validation.invalidPhone');
        if (!isEmpty(customerData.website) && !urlPattern.test(customerData.website))
            errors.website = t('customers.validation.invalidWebsite');
        if (!isEmpty(customerData.foundationYear) && (
            isNaN(customerData.foundationYear) ||
            Number(customerData.foundationYear) < 1800 ||
            Number(customerData.foundationYear) > currentYear
        )) errors.foundationYear = t('customers.validation.invalidFoundationYear', { year: currentYear });
        if (isEmpty(customerData.address)) errors.address = t('customers.validation.addressRequired');

        const contact = customerData.customerContact[0];
        if (!contact) {
            errors.customerContact = t('customers.validation.contactRequired');
        } else {
            if (isEmpty(contact.firstName)) errors.contactFirstName = t('customers.validation.firstNameRequired');
            if (isEmpty(contact.lastName))  errors.contactLastName  = t('customers.validation.lastNameRequired');
            if (isEmpty(contact.email))     errors.contactEmail     = t('customers.validation.emailRequired');
            else if (!emailPattern.test(contact.email)) errors.contactEmail = t('customers.validation.invalidEmail');
            if (!isEmpty(contact.phone) && !phonePattern.test(contact.phone)) errors.contactPhone = t('customers.validation.invalidPhone');
            if (isEmpty(contact.address))   errors.contactAddress   = t('customers.validation.addressRequired');
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const [isSaving, setIsSaving] = useState(false);

    const handleCreateCustomer = async () => {
        if (!validateInputs()) {
            showToast(t('customers.messages.validationFailed'), 'warning');
            return;
        }
        setIsSaving(true);
        try {
            const response = await CustomerService.createCustomer(customerData);
            if (response) {
                setCustomers(prev => [response, ...prev]);
                setTotalNumberOfCustomers(n => n + 1);
                handleCloseDrawer();
                showToast(t('customers.messages.created'), 'success');
            } else {
                showToast(t('customers.messages.createError'), 'error');
            }
        } catch (error) {
            console.error('Error creating customer:', error);
            showToast(t('customers.messages.createError'), 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // Row actions
    const handleViewCustomer = (customer) => navigate(`/customers/${customer.id}`);

    const handleMoreClick = (event, customer) => {
        setAnchorCustomerEl(event.currentTarget);
        setSelectedCustomer(customer);
    };
    const handleClosePopover = () => setAnchorCustomerEl(null);

    const handleDeleteCustomer = (customer) => {
        setAnchorCustomerEl(null);
        setSelectedCustomer(customer);
        setConfirmDeleteOpen(true);
    };

    const handleConfirmDelete = async () => {
        setIsDeletingCustomer(true);
        try {
            await CustomerService.deleteCustomer(selectedCustomer.id);
            setConfirmDeleteOpen(false);
            setCustomers(prev => prev.filter(c => c.id !== selectedCustomer.id));
            setTotalNumberOfCustomers(n => n - 1);
            showToast(t('customers.messages.deleted'), 'warning');
        } catch (error) {
            console.error(error);
            setIsDeletingCustomer(false);
        }
    };

    return (
        <Layout>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <PageHeader
                    title={t('customers.title')}
                    count={totalNumberOfCustomers}
                    action={
                        <Button
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            onClick={() => setOnboardingOpen(true)}
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, borderColor: '#CBD5E1', color: '#427898' }}
                        >
                            Onboard via invoice
                        </Button>
                    }
                />
                <InvoiceOnboardingModal
                    open={isOnboardingOpen}
                    onClose={(customerId) => {
                        setOnboardingOpen(false);
                        if (customerId) {
                            fetchPage(0, activeSearch);
                            clearTimeout(highlightTimerRef.current);
                            setHighlightId(customerId);
                            highlightTimerRef.current = setTimeout(() => setHighlightId(null), 2000);
                        }
                    }}
                />

                {/* Search toolbar */}
                <div className={styles.toolbar}>
                    <div className={styles.searchRow}>
                        <TextField
                            label={t('customers.search')}
                            size="small"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<SearchIcon />}
                            onClick={handleSearch}
                            sx={{ color: '#427898', borderColor: '#427898' }}
                        >
                            {t('customers.search')}
                        </Button>
                    </div>
                </div>

                {loadingCustomers ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
                        <CircularProgress size={40} sx={{ color: '#427898' }} />
                    </Box>
                ) : (
                    <>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' },
                            gap: 2,
                        }}>
                            {/* Add-new tile */}
                            <Box
                                onClick={handleOpenDrawer}
                                sx={{
                                    border: '1.5px dashed #CBD5E1', borderRadius: 2, p: 2.5,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                    gap: 1, minHeight: 140, cursor: 'pointer', transition: 'all 0.15s',
                                    '&:hover': { borderColor: '#427898', bgcolor: '#F8FBFF' },
                                }}
                            >
                                <Box sx={{
                                    width: 38, height: 38, borderRadius: '50%', bgcolor: '#F0F7FF', color: '#427898',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <AddIcon fontSize="small" />
                                </Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#427898' }}>
                                    {t('customers.add')}
                                </Typography>
                            </Box>

                            {customers.map((customer) => {
                                const initials = (customer.name || '')
                                    .split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
                                const hue = [...(customer.name || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
                                return (
                                    <Paper
                                        key={customer.id}
                                        elevation={0}
                                        onClick={() => handleViewCustomer(customer)}
                                        sx={{
                                            border: '1px solid #EEF2F6', borderRadius: 2, p: 2.5, cursor: 'pointer',
                                            display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 140,
                                            transition: 'border-color 0.15s, box-shadow 0.15s',
                                            '&:hover': { borderColor: '#CBD5E1', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
                                            '&:hover .card-actions': { opacity: 1 },
                                            '& .card-actions': { opacity: 0, transition: 'opacity 0.15s' },
                                            ...(customer.id === highlightId && {
                                                '@keyframes highlightFade': {
                                                    '0%': { backgroundColor: '#D1FAE5' },
                                                    '100%': { backgroundColor: 'transparent' },
                                                },
                                                animation: 'highlightFade 2s ease-out forwards',
                                            }),
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{
                                                width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                                                bgcolor: `hsl(${hue}, 55%, 88%)`, color: `hsl(${hue}, 55%, 32%)`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 13, fontWeight: 700,
                                            }}>
                                                {initials}
                                            </Box>
                                            <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: '#0F172A' }}>
                                                {customer.name}
                                            </Typography>
                                            <IconButton
                                                className="card-actions"
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); handleMoreClick(e, customer); }}
                                                sx={{ color: '#94A3B8', flexShrink: 0 }}
                                            >
                                                <MoreHorizIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            {customer.email && (
                                                <Typography noWrap sx={{ fontSize: 12.5, color: '#64748B' }}>{customer.email}</Typography>
                                            )}
                                            {customer.phone && (
                                                <Typography noWrap sx={{ fontSize: 12.5, color: '#94A3B8', mt: 0.3 }}>{customer.phone}</Typography>
                                            )}
                                            {customer.address && (
                                                <Typography noWrap sx={{ fontSize: 12.5, color: '#94A3B8', mt: 0.3 }}>{customer.address}</Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>

                        {customers.length === 0 && activeSearch && (
                            <Typography sx={{ textAlign: 'center', color: '#94A3B8', fontSize: 13, mt: 4 }}>
                                {t('customers.noResults')}
                            </Typography>
                        )}

                        {customers.length < totalNumberOfCustomers && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                    startIcon={loadingMore ? <CircularProgress size={16} sx={{ color: '#94A3B8' }} /> : null}
                                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, borderColor: '#CBD5E1', color: '#427898' }}
                                >
                                    {t('customers.loadMore')} ({totalNumberOfCustomers - customers.length})
                                </Button>
                            </Box>
                        )}
                    </>
                )}

                <Popover
                    open={Boolean(anchorCustomerEl)}
                    anchorEl={anchorCustomerEl}
                    onClose={handleClosePopover}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <MenuItem onClick={() => { handleViewCustomer(selectedCustomer); handleClosePopover(); }}>
                        {t('customers.view')}
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleDeleteCustomer(selectedCustomer)}
                        sx={{ color: 'red' }}
                    >
                        {t('customers.delete')}
                    </MenuItem>
                </Popover>
            </Box>

            {/* ── Create Customer Drawer ── */}
            <Drawer
                anchor="right"
                open={isDrawerOpen}
                onClose={handleCloseDrawer}
                PaperProps={{ sx: { width: { xs: '100vw', sm: 520 }, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 32px rgba(15,23,42,0.12)' } }}
                slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
            >
                <Box sx={{ height: 3, bgcolor: '#427898', flexShrink: 0 }} />

                {/* Header */}
                <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography fontWeight={700} fontSize={16} color="#0F172A">
                            {t('customers.add')}
                        </Typography>
                        <Typography fontSize={12} color="#94A3B8" mt={0.3}>
                            {t('customers.info')} + {t('customers.contacts')}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={handleCloseDrawer} sx={{ color: '#94A3B8', mt: -0.25 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>

                    {/* Section 01 */}
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#427898', letterSpacing: 1.5, textTransform: 'uppercase', mb: 1.5 }}>
                        01 — {t('customers.info')}
                    </Typography>

                    <TextField
                        fullWidth size="small" label={t('customers.name')}
                        value={customerData.name}
                        onChange={(e) => setCustomerData(prev => ({ ...prev, name: e.target.value }))}
                        error={!!validationErrors.name}
                        helperText={validationErrors.name || ' '}
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            fullWidth size="small" label={t('customers.email')}
                            value={customerData.email}
                            onChange={(e) => setCustomerData(prev => ({ ...prev, email: e.target.value }))}
                            error={!!validationErrors.email}
                            helperText={validationErrors.email || ' '}
                        />
                        <TextField
                            fullWidth size="small" label={t('customers.phone')}
                            value={customerData.phone}
                            onChange={(e) => setCustomerData(prev => ({ ...prev, phone: e.target.value }))}
                            error={!!validationErrors.phone}
                            helperText={validationErrors.phone || ' '}
                        />
                        <TextField
                            fullWidth size="small" label="Website URL"
                            value={customerData.website}
                            onChange={(e) => setCustomerData(prev => ({ ...prev, website: e.target.value }))}
                            error={!!validationErrors.website}
                            helperText={validationErrors.website || ' '}
                        />
                        <TextField
                            fullWidth size="small" label={t('customers.foundationYear')}
                            value={customerData.foundationYear}
                            onChange={(e) => setCustomerData(prev => ({ ...prev, foundationYear: e.target.value }))}
                            error={!!validationErrors.foundationYear}
                            helperText={validationErrors.foundationYear || ' '}
                        />
                        <TextField
                            fullWidth size="small" label={t('customers.employeesNr')}
                            value={customerData.employeesNr}
                            onChange={(e) => setCustomerData(prev => ({ ...prev, employeesNr: e.target.value }))}
                            helperText=" "
                        />
                    </Box>
                    <AddressAutocomplete
                        customerData={customerData}
                        setCustomerData={setCustomerData}
                        error={!!validationErrors.address}
                        helperText={validationErrors.address || ' '}
                    />

                    <Divider sx={{ my: 2 }} />

                    {/* Section 02 */}
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#427898', letterSpacing: 1.5, textTransform: 'uppercase', mb: 1.5 }}>
                        02 — {t('customers.contacts')}
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            fullWidth size="small" label={t('customers.firstName')}
                            value={customerData.customerContact[0].firstName}
                            onChange={(e) => updateContact('firstName', e.target.value)}
                            error={!!validationErrors.contactFirstName}
                            helperText={validationErrors.contactFirstName || ' '}
                        />
                        <TextField
                            fullWidth size="small" label={t('customers.lastName')}
                            value={customerData.customerContact[0].lastName}
                            onChange={(e) => updateContact('lastName', e.target.value)}
                            error={!!validationErrors.contactLastName}
                            helperText={validationErrors.contactLastName || ' '}
                        />
                        <TextField
                            fullWidth size="small" label={t('customers.email')}
                            value={customerData.customerContact[0].email}
                            onChange={(e) => updateContact('email', e.target.value)}
                            error={!!validationErrors.contactEmail}
                            helperText={validationErrors.contactEmail || ' '}
                        />
                        <TextField
                            fullWidth size="small" label={t('customers.phone')}
                            value={customerData.customerContact[0].phone}
                            onChange={(e) => updateContact('phone', e.target.value)}
                            error={!!validationErrors.contactPhone}
                            helperText={validationErrors.contactPhone || ' '}
                        />
                    </Box>
                    <TextField
                        fullWidth size="small" label={t('customers.address')}
                        value={customerData.customerContact[0].address}
                        onChange={(e) => updateContact('address', e.target.value)}
                        error={!!validationErrors.contactAddress}
                        helperText={validationErrors.contactAddress || ' '}
                    />

                </Box>

                {/* Footer */}
                <Box sx={{ px: 3, py: 2.5, flexShrink: 0, borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button onClick={handleCloseDrawer} sx={{ textTransform: 'none', color: '#64748b', fontWeight: 500 }}>
                        {t('customers.dismiss')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateCustomer}
                        disabled={isSaving}
                        sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none', px: 2.5, '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}
                    >
                        {t('customers.create')}
                    </Button>
                </Box>
            </Drawer>

            <ConfirmModal
                open={isConfirmDeleteOpen}
                onClose={() => !isDeletingCustomer && setConfirmDeleteOpen(false)}
                onConfirm={handleConfirmDelete}
                question={`${t('customers.delete')}: "${selectedCustomer?.name}"!`}
                isLoading={isDeletingCustomer}
            />
        </Layout>
    );
};

export default CustomersPage;
